import { Voice, body, transient, vary } from '../audio-engine';
export { alarm, beam, boom, dash, hurt, level, missile, portal, preEvo, revive, slash } from './thud';
/* ============================================================
   配方：乾淨脆響 CRISP —— 2026-08-06 使用者：「工業太悶、有調性太怪不搭」
   ============================================================

   ## 這一版在解什麼

   前兩版各自撞到一邊的牆：

   | 版本 | 能量落點 | 使用者評語 | 病因 |
   |---|---|---|---|
   | 撞擊優先（初版） | 白噪 2–5 kHz | 「不好聽，很躁」 | 那是耳朵最敏感也最疲勞的頻段 |
   | 悶厚工業 `thud` | 38–150 Hz | 「太悶」 | 能量全壓到低頻，密度一高就變低頻泥巴 |
   | 有調性 `tonal` | 音階 | 「太怪不搭」 | 音程關係＝旋律感，硬邊機甲讀起來像玩具 |

   **中間那一段沒有人用過**：400 Hz – 1.6 kHz。那裡人耳分辨力最好，
   而且既不悶（>300 Hz）也不刺（<2 kHz）。這一版整套住在那裡。

   ## 三條規則

   1. **能量在中頻，用 `bandpass` 不用 `lowpass`。**
      低通是「把高頻砍掉」（剩下的就是悶），帶通是「只留這一段」（有焦點）。
   2. **短。** 每個音都比 `thud` 短一半以上 —— 擊殺本體 70 ms（thud 是 190），
      命中 8 ms。短本身就是「乾淨」：尾巴才是糊掉的來源，不是音色。
   3. **不要音程。** 音高幾乎不滑（命中 760→620 只掉三度不到），
      所以聽不出調性，也就不會有 `tonal` 那種「在演奏」的違和。

   其餘十一個音沿用 `thud`（上面那行 re-export）—— 這一輪要比的是
   **每秒響幾十次的那三個**，把不相干的變因換掉會讓 A/B 讀不出結論。 */

/** 主砲：中頻的「tick」＋一點點中低支撐。**沒有任何 2 kHz 以上的東西**，
    也沒有任何 300 Hz 以下的東西 —— 前者刺，後者悶。 */
export function shoot(v: Voice) {
  transient({
    bus: 'weapon', x: v.x, z: v.z, vol: 0.75, type: 'bandpass', q: 1.6,
    dur: vary(0.005, 0.2), f0: vary(1500, 0.10), f1: vary(900, 0.10)
  });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.55, dur: vary(0.028, 0.15), f0: vary(420, 0.08), f1: 300, type: 'triangle' });
}

/** 命中：一個 8 ms 的中頻點。

    `thud` 的命中是 430→190 Hz、22 ms —— 密集時那是一團低頻。
    這一版把它抬到 760 Hz 並砍到 8 ms：**聽得見「打到了」，但不佔任何空間**。
    音高只掉一點點（760→620），所以不會讀成音符。 */
export function hit(v: Voice) {
  var im = v.imp ? 1 : 0;
  body({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.34 + im * 0.20,
    dur: vary(0.008 + im * 0.006, 0.22), f0: vary(760 + im * 120, 0.10), f1: 620, type: 'sine', drop: 0.25
  });
}

/** 擊殺：中頻的短促「啪」＋一段收得很快的下墜。

    `v.n` = 這個音代表幾隻（密度層聚合的結果）。數量一律用**質地**表達 ——
    n 大就多補一顆錯開的點，而不是加音量或加低頻（那會走回「悶」）。 */
export function kill(v: Voice) {
  var n = Math.max(1, v.n || 1);
  var k = Math.min(1, (n - 1) / 6);
  var im = v.imp ? 1 : 0;
  transient({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.7 + k * 0.12 + im * 0.12, type: 'bandpass', q: 1.4 - k * 0.4,
    dur: vary(0.009 + k * 0.006, 0.2), f0: vary(1150 + k * 350, 0.12), f1: vary(700, 0.12)
  });
  body({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.8, dur: vary(0.07 - k * 0.015 + im * 0.04, 0.12),
    f0: vary(340, 0.10), f1: 170, type: 'sine', drop: 0.35
  });
  /* 三隻以上補一顆錯開 22 ms 的點。「一片」與「一隻」的差別是層次不是響度。 */
  if (n >= 3) setTimeout(function () {
    transient({
      bus: 'enemy', x: v.x, z: v.z, vol: 0.3 + k * 0.22, type: 'bandpass', q: 1.8,
      dur: 0.007, f0: vary(1000, 0.18), f1: 640
    });
  }, 22);
}
